import React, { useState, useEffect, useMemo } from "react";
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel, faPrint } from '@fortawesome/free-solid-svg-icons';
import './AllLeads.css';

import useTableSelection from './shared/useTableSelection';
import TableToolbar from './shared/TableToolbar';
import { exportToPrint, exportToExcel, exportToPDF } from './shared/exportTable';

const AllLeads = ({ openEditDrawer, refreshTrigger, filter = 'all' }) => {    
    const [leadsData, setLeadsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const userType = localStorage.getItem("usertype"); 
    const loggedInUserId = localStorage.getItem("id") || localStorage.getItem("user_id");
    const loggedInFullName = (localStorage.getItem("fname") + " " + localStorage.getItem("lname")).trim();

    const fetchLeads = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get('https://skcapi.btcjapan.co/get_leads.php', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                let fetchedLeads = res.data.data;
                
                if (userType === 'administrator') {
                    // Admin sees all
                } 
                else if (userType === 'leader') {
                    let leadersGroupId = null;
                    for (const lead of fetchedLeads) {
                        if (lead.assigned_users && Array.isArray(lead.assigned_users)) {
                            const found = lead.assigned_users.find(u => String(u.id) === String(loggedInUserId));
                            if (found && lead.group_id) {
                                leadersGroupId = lead.group_id;
                                break; 
                            }
                        }
                    }
                    if (leadersGroupId) {
                        fetchedLeads = fetchedLeads.filter(lead => lead.group_id === leadersGroupId);
                    } else {
                        fetchedLeads = []; 
                    }
                } 
                else if (userType === 'member') {
                    fetchedLeads = fetchedLeads.filter(lead => 
                        (lead.assigned_users && Array.isArray(lead.assigned_users) && 
                        lead.assigned_users.some(u => String(u.id) === String(loggedInUserId)))
                        ||
                        (lead.reporter && lead.reporter.toLowerCase() === loggedInFullName.toLowerCase())
                    );
                }

                setLeadsData(fetchedLeads);
            } else {
                setLeadsData([]);
            }
        } catch (err) { 
            console.error("Fetch Error:", err); 
            setLeadsData([]); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => {
        fetchLeads();
    }, [refreshTrigger]);

    // ==================== NEW FUNCTION: Handle Status Change & Convert to Client ====================
    const handleStatusChange = async (leadId, newStatus) => {
        if (newStatus === 'Converted') {
            const isConfirmed = window.confirm("Are you sure you want convert the lead to client?");
            if (!isConfirmed) {
                fetchLeads();
                return;
            }
        }

        try {
            const token = localStorage.getItem("token");
            if (newStatus === 'Converted') {
                const res = await axios.post('https://skcapi.btcjapan.co/convert_lead_to_client.php', { lead_id: leadId }, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                });

                if (res.data.success) {
                    alert("Lead converted to client successfully!");
                } else {
                    alert(res.data.message || "Failed to convert lead.");
                }
            } else {
                // Just update status normally
                await axios.post('https://skcapi.btcjapan.co/update_lead_status.php', { lead_id: leadId, lead_status: newStatus }, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
            }

            fetchLeads();
        } catch (err) {
            console.error(err);
            alert("Error updating lead status.");
        }
    };
    // =============================================================================

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this lead?")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.post('https://skcapi.btcjapan.co/delete_lead.php', { id }, { headers: { Authorization: `Bearer ${token}` } });
            fetchLeads();
        } catch (err) {
            alert("Error deleting lead");
            console.error(err);
        }
    };

    // ==================== APPLY DROPDOWN FILTER + SEARCH ====================
    const filteredLeads = useMemo(() => {
        // Step 1 — apply the dropdown filter (from Leads.js)
        let result = leadsData;

        if (filter === 'me') {
            result = result.filter(lead =>
                Array.isArray(lead.assigned_users) &&
                lead.assigned_users.some(u => String(u.id) === String(loggedInUserId))
            );
        } else if (filter === 'everyone') {
            result = result.filter(lead => lead.assigned_to_type === 'everyone');
        } else if (filter.startsWith('source:')) {
            const src = filter.split(':')[1].toLowerCase();
            result = result.filter(lead => (lead.lead_source || '').toLowerCase() === src);
        } else if (filter.startsWith('status:')) {
            const st = filter.split(':')[1].toLowerCase();
            result = result.filter(lead => (lead.lead_status || '').toLowerCase() === st);
        }

        // Step 2 — apply the search term on top of the filtered result
        const term = searchTerm.toLowerCase();
        if (!term) return result;

        return result.filter(lead =>
            (lead.lead_name || "").toLowerCase().includes(term) ||
            (lead.lead_email || "").toLowerCase().includes(term) ||
            (lead.lead_phone || "").includes(term) ||
            (lead.lead_source || "").toLowerCase().includes(term) ||
            (lead.lead_status || "").toLowerCase().includes(term) ||
            (lead.lead_title || "").toLowerCase().includes(term)
        );
    }, [leadsData, filter, searchTerm, loggedInUserId]);

    const selection = useTableSelection(filteredLeads, (row) => row.id);

    const exportColumns = [
        'Lead Title',
        'Lead Name',
        'Email',
        'Phone',
        'Source',
        'Status',
        'Reporter',
    ];

    const buildExportRows = (rows) => rows.map(lead => [
        lead.lead_title || '',
        lead.lead_name || '',
        lead.lead_email || '',
        lead.lead_phone || '',
        lead.lead_source || '',
        lead.lead_status || '',
        lead.reporter || '',
    ]);

    const handleExport = (kind) => {
        const rows = selection.selectedRows;
        if (rows.length === 0) return;
        const title = 'Leads Export';
        const data = buildExportRows(rows);
        if (kind === 'print') exportToPrint(title, exportColumns, data);
        if (kind === 'excel') exportToExcel(title, exportColumns, data);
        if (kind === 'pdf')   exportToPDF(title, exportColumns, data);
    };

        return (
        <div className="all-leads-container">
            {/* ===== TOOLBAR ===== */}
            <TableToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                isSelectMode={selection.isSelectMode}
                onToggleSelectMode={selection.toggleSelectMode}
                onSelectAll={() =>
                    selection.allSelected ? selection.clearSelection() : selection.selectAll()
                }
                selectedCount={selection.selectedIds.size}
                allSelected={selection.allSelected}
                onPrint={() => handleExport('print')}
                onExcel={() => handleExport('excel')}
                onPDF={() => handleExport('pdf')}
            />

            <div className="table-responsive">
                <table className="leads-table">
                    <thead>
                        <tr>
                            {/* CHECKBOX COLUMN — only renders in select mode */}
                            {selection.isSelectMode && (
                                <th style={{ width: 42, textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={selection.allSelected}
                                        onChange={() =>
                                            selection.allSelected
                                                ? selection.clearSelection()
                                                : selection.selectAll()
                                        }
                                        title={selection.allSelected ? 'Unselect All' : 'Select All'}
                                    />
                                </th>
                            )}
                            <th>Lead Title</th>
                            <th>Lead Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Source / Link</th>
                            <th>Status</th>
                            <th>Assigned To</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td
                                    colSpan={selection.isSelectMode ? 9 : 8}
                                    style={{ textAlign: "center", padding: 40, color: "#888" }}
                                >
                                    Loading leads...
                                </td>
                            </tr>
                        ) : filteredLeads.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={selection.isSelectMode ? 9 : 8}
                                    style={{ textAlign: "center", padding: 40, color: "#888" }}
                                >
                                    {leadsData.length === 0
                                        ? "No leads found."
                                        : "No leads match the current filter."}
                                </td>
                            </tr>
                        ) : (
                            filteredLeads.map(lead => (
                                <tr key={lead.id}>
                                    {/* CHECKBOX CELL — only in select mode */}
                                    {selection.isSelectMode && (
                                        <td
                                            style={{ textAlign: 'center' }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selection.isRowSelected(lead.id)}
                                                onChange={() => selection.toggleRow(lead.id)}
                                            />
                                        </td>
                                    )}
                                    <td>{lead.lead_title || 'New Lead'}</td>
                                    <td>
                                        <a
                                            href="#"
                                            className="lead-title"
                                            onClick={(e) => { e.preventDefault(); openEditDrawer(lead.id); }}
                                        >
                                            {lead.lead_name}
                                        </a>
                                    </td>
                                    <td>{lead.lead_email}</td>
                                    <td>{lead.lead_phone}</td>
                                    <td>
                                        <div>{lead.lead_source || '-'}</div>
                                        {lead.lead_link && (
                                            <a
                                                href={lead.lead_link}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ fontSize: '12px', color: '#007bff' }}
                                            >
                                                Link
                                            </a>
                                        )}
                                    </td>
                                    <td>
                                        <select
                                            value={lead.lead_status || 'New'}
                                            onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                                            style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                                        >
                                            <option>New</option>
                                            <option>Contacted</option>
                                            <option>Qualified</option>
                                            <option>Converted</option>
                                            <option>Lost</option>
                                        </select>
                                    </td>
                                    <td>
                                        <div className="avatar-group">
                                            {lead.assigned_to_type === 'everyone' ? (
                                                <span style={{ background: '#6f42c1', color: '#fff', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
                                                    Everyone
                                                </span>
                                            ) : (
                                                <>
                                                    {lead.assigned_users?.slice(0, 4).map((user, idx) => (
                                                        <div
                                                            key={idx}
                                                            style={{ width: 30, height: 30, borderRadius: '50%', background: user.image ? 'transparent' : '#007bff', overflow: 'hidden', border: '2px solid #fff' }}
                                                        >
                                                            {user.image ? (
                                                                <img src={user.image} alt={user.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            ) : (
                                                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px' }}>
                                                                    {user.name?.charAt(0)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                    <span style={{ fontSize: '12px', color: '#555', marginLeft: '5px' }}>
                                                        Group #{lead.group_id}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="action-btns">
                                            <button className="action-icon-btn edit" onClick={() => openEditDrawer(lead.id)}>✎</button>
                                            <button className="action-icon-btn delete" onClick={() => handleDelete(lead.id)}>🗑</button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AllLeads;